import Decimal from 'decimal.js';

import { AfterDeath } from '@/types/schema';
import { PathsOf } from '@/types/subform';

import { CommonHypotheticalTransferFormShape } from '../EstateWaterfallHypotheticalTransfers/estateWaterfallHypotheticalTransfers.types';

export type HypotheticalSaleLoanFormTab =
  | 'basicInformation'
  | 'assets'
  | 'payment';

export type HypotheticalSaleLoanFormPaymentModel =
  | 'amortizing'
  | 'interestOnlyWithBalloon'
  | 'lumpSum';

export type HypotheticalSaleLoanFormFundingKind = 'proRata' | 'assets';

export interface HypotheticalSaleLoanFormShape
  extends CommonHypotheticalTransferFormShape {
  // DISPLAY-ONLY PROPERTIES
  _kind: HypotheticalSaleLoanFormKind;

  // ASSUMPTIONS PROPERTIES
  // If set, projections will run with these assumptions instead of the waterfall's
  firstGrantorDeathYear: number | null;
  secondGrantorDeathYear: number | null;
  growthProfileOverrideId: string | null;

  // ILLUSTRATION PROPERTIES
  illustrationScenario: AfterDeath;
  scheduleDisplayType: 'paymentSchedule' | 'projectedGrowth';

  // FORM PROPERTIES
  /** The seller, an entity or individual */
  sellerId: string;
  /** The recipient, an entity or individual */
  recipientId: string;
  /** The start date  */
  startDate: Date | null;
  /** The term length in years. Should be a number, but the form input represents it as a string */
  termLength: string;
  /** The AFR interest rate */
  applicableInterestRate: Decimal | null;
  pretermGrantorDeathHandling: 'repaid' | 'canceled';
  /** The payment model, exposed on the payment tab */
  paymentModel: HypotheticalSaleLoanFormPaymentModel | null;
  /** If true, the transfer will be liquidated to cash */
  forceLiquidationOnTransfer: boolean;
}

export type HypotheticalSaleLoanFormPaths =
  PathsOf<HypotheticalSaleLoanFormShape>;

export type HypotheticalSaleLoanFormKind = 'sale' | 'loan';

type SaleLoanFormField = keyof HypotheticalSaleLoanFormShape;

export const fieldTabMap: Record<
  SaleLoanFormField,
  HypotheticalSaleLoanFormTab | null
> = {
  // display-only
  _kind: null,

  // assumptions aren't part of the form and won't error
  firstGrantorDeathYear: null,
  secondGrantorDeathYear: null,
  growthProfileOverrideId: null,

  // illustration; not part of the form and won't error
  illustrationScenario: null,
  scheduleDisplayType: null,

  // basicInformation
  displayName: 'basicInformation',
  sellerId: 'basicInformation',
  recipientId: 'basicInformation',
  startDate: 'basicInformation',
  termLength: 'basicInformation',
  applicableInterestRate: 'basicInformation',
  pretermGrantorDeathHandling: 'basicInformation',

  // assets
  fundingKind: 'assets',
  proRataPattern: 'assets',
  proRataAmount: 'assets',
  proRataPercent: 'assets',
  assetsPattern: 'assets',
  selectedAssetClassesAndBusinessIds: 'assets',
  selectedAssetClassAndBusinessValues: 'assets',
  applyEntityTaxDiscount: 'assets',
  entityTaxDiscountRate: 'assets',
  forceLiquidationOnTransfer: 'assets',

  // payment
  paymentModel: 'payment',
};
